/**
 * `/v1/beneficiaries` — the people you pay who bank elsewhere (go-wallet#1064).
 *
 * # This is not `saved-recipients`, and the difference is structural
 *
 * A saved recipient holds a Tesora account, so the backend keeps only a pointer
 * to the account row that already has their details and re-resolves it on every
 * read — which is why that list carries `payable` and withholds the address the
 * moment somebody stops resolving. There is no such row for a payee at another
 * bank. The name, the IBAN and the optional BIC ARE the entry, and every field
 * is present on every one of them.
 *
 * # The address is sealed at rest, and none of that shows here
 *
 * The backend refuses to store a payment destination as a readable column — two
 * of its own guards say so — and seals it under a key destroyed when a customer
 * is erased. That is a property of the table, not of this contract: what arrives
 * here is the address the customer typed, because it is theirs. The only visible
 * consequence is `masked`, which is what a list should render, and the fact that
 * a deployment with no key mounts no routes at all.
 *
 * # 404 means "not configured here", not "you have kept nobody"
 *
 * An empty address book answers `200 {"beneficiaries": []}`. A deployment
 * without the sealing key does not mount the routes, so it answers 404 — and
 * `unavailable` is the honest state for that, never an empty list. Rendering
 * "you have not added anyone yet" to somebody whose backend cannot store one is
 * the mistake `accounts.ts` keeps `none` and `unavailable` apart to prevent.
 */
import { customerMessage } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

export interface Beneficiary {
  /** The ENTRY's reference, never the payee's. Opaque; never parsed. */
  readonly reference: string
  /** Your own word for them. */
  readonly label: string
  /**
   * The name their bank holds, which is NOT the label.
   *
   * Rendered alongside the label wherever somebody is about to pay: a screen
   * showing only a nickname lets somebody instruct a payment without ever
   * seeing the name it will carry.
   */
  readonly creditorName: string
  /** The normalised account number — upper case, no separators. */
  readonly creditorIBAN: string
  /** Absent when none was given, which is the ordinary SEPA case. */
  readonly creditorBIC?: string
  /** Country prefix and last four. Never enough to be paid into. */
  readonly masked: string
}

export type BeneficiariesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly beneficiaries: readonly Beneficiary[] }
  /** We could not look. NEVER rendered as "you have kept nobody". */
  | { readonly status: 'unavailable'; readonly reason: string }

interface BeneficiaryDTO {
  reference?: unknown
  label?: unknown
  creditor_name?: unknown
  creditor_iban?: unknown
  creditor_bic?: unknown
  masked?: unknown
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

/**
 * Decode one entry, or say what was wrong with it.
 *
 * Every field the contract marks required is checked and none is defaulted. A
 * missing `creditor_name` rendered as blank would show a payee nobody can
 * identify beside an account number they are about to send money to.
 */
export function decodeBeneficiary(dto: BeneficiaryDTO): Beneficiary | string {
  const reference = str(dto.reference)
  if (reference === undefined) return 'a payee arrived with no reference'

  const label = str(dto.label)
  if (label === undefined) return `payee ${reference} arrived with no name`

  const creditorName = str(dto.creditor_name)
  if (creditorName === undefined) return `payee ${reference} arrived with no account name`

  const creditorIBAN = str(dto.creditor_iban)
  if (creditorIBAN === undefined) return `payee ${reference} arrived with no account number`

  const masked = str(dto.masked)
  if (masked === undefined) return `payee ${reference} arrived with nothing to show in a list`

  // ABSENT, never blank. The contract omits the field rather than sending an
  // empty string, and an empty string here would render as a BIC of nothing.
  const creditorBIC = str(dto.creditor_bic)

  return { reference, label, creditorName, creditorIBAN, masked, ...(creditorBIC ? { creditorBIC } : {}) }
}

export async function fetchBeneficiaries(api: WalletApi): Promise<BeneficiariesState> {
  let dto: { beneficiaries?: unknown }
  try {
    dto = await api.get<{ beneficiaries?: unknown }>('/v1/beneficiaries')
  } catch (err) {
    return { status: 'unavailable', reason: customerMessage(err, 'Please try again in a moment.') }
  }

  const raw = dto?.beneficiaries
  if (!Array.isArray(raw)) {
    // NOT an empty list. A body without the array is a contract the app does not
    // recognise, and "you have added nobody" is a claim it has no basis for.
    return { status: 'unavailable', reason: 'the list came back in a shape this app does not know' }
  }

  const out: Beneficiary[] = []
  for (const item of raw) {
    const one = decodeBeneficiary((item ?? {}) as BeneficiaryDTO)
    // ONE BAD ENTRY FAILS THE WHOLE LIST. A list quietly missing the payee it
    // could not read is one somebody re-adds, and the backend would then hold
    // two names for one account — which it is specifically built to avoid.
    if (typeof one === 'string') return { status: 'unavailable', reason: one }
    out.push(one)
  }
  return { status: 'ready', beneficiaries: out }
}

export interface NewBeneficiary {
  readonly label: string
  readonly creditorName: string
  readonly creditorIBAN: string
  readonly creditorBIC?: string
}

/**
 * Save a payee, or update one already saved.
 *
 * Returns the entry, or a sentence to put on the form. The backend tells the
 * four refusals apart — label, account name, IBAN, BIC — because a person can
 * act on three of them differently, so this does not collapse them.
 */
export async function saveBeneficiary(
  api: WalletApi,
  b: NewBeneficiary,
): Promise<Beneficiary | string> {
  let dto: BeneficiaryDTO
  try {
    dto = await api.post<BeneficiaryDTO>('/v1/beneficiaries', {
      label: b.label,
      creditor_name: b.creditorName,
      creditor_iban: b.creditorIBAN,
      // Omitted rather than sent empty: the contract's absent case is "no BIC",
      // and "" would be a BIC of nothing.
      ...(b.creditorBIC ? { creditor_bic: b.creditorBIC } : {}),
    }, {
      // The database decides, not a header: `beneficiaries` is UNIQUE on
      // (customer_id, destination_digest) with ON CONFLICT DO UPDATE, so saving
      // the same account twice is one row either way and a replay writes what
      // the first write wrote. No money moves on this route.
      replayGuardedBy:
        'UNIQUE (customer_id, destination_digest) with ON CONFLICT DO UPDATE: a replay is the same row',
    })
  } catch (err) {
    return customerMessage(err, 'We could not save that payee. Please try again.')
  }
  const one = decodeBeneficiary(dto ?? {})
  return one
}

/** Rename a payee. The address is not editable — see the route's own note. */
export async function renameBeneficiary(
  api: WalletApi,
  reference: string,
  label: string,
): Promise<Beneficiary | string> {
  let dto: BeneficiaryDTO
  try {
    dto = await api.put<BeneficiaryDTO>(
      `/v1/beneficiaries/${encodeURIComponent(reference)}`,
      { label },
      {
        // An UPDATE of one named row to one given label. Replaying it sets the
        // same label on the same row, so there is no second effect to guard.
        replayGuardedBy: 'an UPDATE of one row to one value: a replay sets what is already set',
      },
    )
  } catch (err) {
    return customerMessage(err, 'We could not rename that payee. Please try again.')
  }
  return decodeBeneficiary(dto ?? {})
}

/** Remove a payee. Returns undefined on success, or a sentence. */
export async function removeBeneficiary(
  api: WalletApi,
  reference: string,
): Promise<string | undefined> {
  try {
    await api.del<unknown>(`/v1/beneficiaries/${encodeURIComponent(reference)}`, {
      // A DELETE of one named row. The second one finds nothing and answers 404;
      // there is no second removal to make.
      replayGuardedBy: 'a DELETE of one named row: the second finds nothing and removes nothing',
    })
  } catch (err) {
    return customerMessage(err, 'We could not remove that payee. Please try again.')
  }
  return undefined
}
